export type ForkApiFn = (
  outcomeId: string,
  title?: string
) => Promise<{ project_id: string; id: string }>;

export interface ForkAndRunOptions {
  outcomeId: string;
  title?: string;
  loggedIn: boolean;
  navigate: (path: string) => void;
  forkApi: ForkApiFn;
}

export interface ForkAndRunResult {
  success: boolean;
  projectId?: string;
  redirectedToLogin?: boolean;
  error?: string;
}

export function studioForkPath(projectId: string): string {
  return `/compose?project=${encodeURIComponent(projectId)}`;
}

export function loginRedirectPath(outcomeId: string): string {
  const next = encodeURIComponent(`/compose?fork=${encodeURIComponent(outcomeId)}`);
  return `/login?next=${next}`;
}

export async function handleForkAndRun(options: ForkAndRunOptions): Promise<ForkAndRunResult> {
  const { outcomeId, title, loggedIn, navigate, forkApi } = options;

  if (!loggedIn) {
    const loginUrl = loginRedirectPath(outcomeId);
    navigate(loginUrl);
    return {
      success: false,
      redirectedToLogin: true,
    };
  }

  try {
    const res = await forkApi(outcomeId, title);
    const projId = res.project_id || res.id;
    const dest = studioForkPath(projId);
    navigate(dest);
    return {
      success: true,
      projectId: projId,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'failed to fork outcome';
    return {
      success: false,
      error: msg,
    };
  }
}

const studioForkInflight = new Map<string, Promise<ForkAndRunResult>>();

export function completeStudioForkOnce(options: ForkAndRunOptions): Promise<ForkAndRunResult> {
  const key = options.outcomeId;
  const existing = studioForkInflight.get(key);
  if (existing) return existing;
  const pending = handleForkAndRun(options).finally(() => {
    studioForkInflight.delete(key);
  });
  studioForkInflight.set(key, pending);
  return pending;
}
